// FILES IN AN AI ANSWER — the `files-recent` view the assistant mounts in its
// thread and a Fly page places as a section (workspace functions/
// assistantAppViews.js, the `views[]` contract).
//
// filesAiViews() is the files view PROVIDER. It rides the shell's EAGER files
// slice WITH its component (workspace sharedLibraries.js), so the view mounts
// at once on any route without folding the files desktop; `fold: 'files'` is
// the safety net for a host that registered the provider alone. One view:
//
//   files-recent   the workspace's files, folders and notes, newest first;
//                  `kind` narrows it, `search` matches the name. A row opens
//                  the item where it lives — the Files window on that node
//                  (`/files?node=<id>`, openFilesDeepLink): a folder entered,
//                  a file in the app that owns its format or the viewer, a
//                  note in the viewer.
//
// The rows come from the files plane's own reads (workspaceProject.fileCanvas
// and docs, `list` — SDK-only) into the view's OWN state, never root.fileNodes.
// Nothing here writes.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function filesAiViews() {
  return {
    app: {
      id: 'system/files',
      navbar: [{ label: 'shell.app.files', route: '/files', icon: 'folder' }],
      apps: [{ icon: 'folder' }]
    },
    fold: 'files',
    views: [
      {
        id: 'files-recent',
        title: 'files.answer.title',
        describe:
          'The workspace files, folders and notes, newest first; kind = images, documents (files and notes), media, folders or notes; search = words in the name.',
        component: 'FilesAnswerView',
        icon: 'folder',
        hue: 'documents',
        size: 'l',
        params: {
          kind: {
            type: 'enum',
            values: ['all', 'image', 'document', 'media', 'folder', 'note'],
            // Bundle keys: the catalog sends them as { en, ka } words.
            labels: {
              all: 'files.answer.kind.all',
              image: 'files.answer.kind.image',
              document: 'files.answer.kind.document',
              media: 'files.answer.kind.media',
              folder: 'files.answer.kind.folder',
              note: 'files.answer.kind.note'
            },
            default: 'all'
          },
          search: { type: 'string', describe: 'words in the file name' },
          limit: { type: 'number', min: 1, max: 50, default: 12 }
        },
        link: { route: '/files', query: {} }
      }
    ]
  }
}

// One item's kind for the view: image | media | document | folder | note |
// other — from the node's kind and its type (the mime, else the extension).
export function filesAnswerKind(item) {
  const it = item && typeof item === 'object' ? item : {}
  if (it.kind === 'folder') return 'folder'
  if (it.kind === 'doc' || it.kind === 'note') return 'note'
  const mime = String(it.mime || '').toLowerCase()
  const ext = (String(it.name || '').match(/\.([a-z0-9]{1,8})$/i) || [])[1] || ''
  const e = ext.toLowerCase()
  if (
    mime.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'heic'].includes(e)
  ) {
    return 'image'
  }
  if (
    mime.startsWith('video/') ||
    mime.startsWith('audio/') ||
    ['mp4', 'mov', 'webm', 'mp3', 'wav', 'm4a', 'ogg'].includes(e)
  ) {
    return 'media'
  }
  if (
    mime === 'application/pdf' ||
    mime.startsWith('text/') ||
    mime.includes('document') ||
    mime.includes('sheet') ||
    mime.includes('presentation') ||
    [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'csv',
      'ppt',
      'pptx',
      'txt',
      'md',
      'rtf',
      'key',
      'pages',
      'numbers'
    ].includes(e)
  ) {
    return 'document'
  }
  return 'other'
}

// Read the items for the view's params: the file_canvas nodes and the notes
// (a note already placed on the desktop is listed once, as its node), the
// kind and search applied, newest first, `limit` of them. `{ items, failed }`
// — failed only when the files read itself failed (notes are best-effort).
export async function filesAnswerRead(sdk, params) {
  const el = this
  const p = params && typeof params === 'object' ? params : {}
  if (!sdk || typeof sdk.execute !== 'function') return { items: [], failed: true }
  const rowsOf = (res) =>
    Array.isArray(res) ? res : (res && Array.isArray(res.data) && res.data) || []
  let failed = false
  const [nodesRes, docsRes] = await Promise.all([
    Promise.resolve()
      .then(() => sdk.execute('workspaceProject.fileCanvas', 'list', {}))
      .catch(() => {
        failed = true
        return []
      }),
    Promise.resolve()
      .then(() => sdk.execute('docs', 'list', {}))
      .catch(() => [])
  ])
  const nodes = rowsOf(nodesRes).map((r) => ({
    id: String(r.id),
    kind: r.kind || 'file',
    name: r.name || '',
    mime: r.mime_type || r.mime || null,
    docId: r.doc_id || null,
    at: Date.parse(r.updated_at || r.created_at || '') || 0
  }))
  const placed = new Set(nodes.filter((n) => n.docId).map((n) => String(n.docId)))
  const notes = rowsOf(docsRes)
    .map((d) => ({
      id: 'doc:' + String(d.docId || d._id || d.id),
      kind: 'doc',
      name: d.title || '',
      mime: null,
      docId: String(d.docId || d._id || d.id),
      at: Date.parse(d.updatedAt || d.createdAt || '') || 0
    }))
    .filter((d) => !placed.has(d.docId))
  const kind = typeof p.kind === 'string' && p.kind ? p.kind : 'all'
  const words = String(p.search || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 12))
  // A DOCUMENT is what a person calls one: an office file (a pdf, a sheet…)
  // OR a note — the Notes app keeps its notes as documents. "Find the
  // document about warranty limitations" asked kind 'document' and found
  // nothing while Notes listed that note (Fly QA, BUG-1: the same file was
  // found with kind 'note'). `note` stays the narrow ask: notes only.
  const fits = (it) => {
    if (kind === 'all') return true
    const k = el.call('filesAnswerKind', it)
    return k === kind || (kind === 'document' && k === 'note')
  }
  const items = nodes
    .concat(notes)
    .filter(fits)
    .filter((it) => {
      const name = String(it.name || '').toLowerCase()
      return words.every((w) => name.includes(w))
    })
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
  return { items, failed }
}

// The view's read, once per params (its OWN state: { files, failed }).
export async function filesAnswerLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  const sig = JSON.stringify([params.kind || 'all', params.search || '', params.limit || 12])
  if (host.__filesAnswerSig === sig) return null
  host.__filesAnswerSig = sig
  const sdk = host.sdk || host.call('getSDK')
  const res = await host.call('filesAnswerRead', sdk, params)
  if (host.__filesAnswerSig !== sig) return null
  const items = res && Array.isArray(res.items) ? res.items : []
  host.state.update({ files: items, failed: !!(res && res.failed) && !items.length })
  return res
}

// LIST rows (the home widget kit's HomeWidgetRow): the name, "kind · when",
// an icon by kind; a row opens the item in the Files window. Every key a row
// reads is set (a childrenAs-'state' row falls back to ROOT for a missing one).
export function filesAnswerRows(items) {
  const el = this
  const list = Array.isArray(items) ? items : []
  const KIND = {
    image: { icon: 'images', label: 'files.answer.kind.image' },
    media: { icon: 'video', label: 'files.answer.kind.media' },
    document: { icon: 'document', label: 'files.answer.kind.document' },
    folder: { icon: 'folder', label: 'files.answer.kind.folder' },
    note: { icon: 'document', label: 'files.answer.kind.note' },
    other: { icon: 'document', label: 'files.answer.kind.other' }
  }
  return list.map((it) => {
    const kind = KIND[el.call('filesAnswerKind', it)] || KIND.other
    const when = it.at
      ? el.call('localizedDate', new Date(it.at), { month: 'short', day: 'numeric' })
      : ''
    return {
      key: 'f-' + String(it.id).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: it.name || String(el.call('polyglot', 'files.answer.untitled')),
      meta: [String(el.call('polyglot', kind.label)), when].filter(Boolean).join(' · '),
      icon: kind.icon,
      initials: null,
      src: null,
      progress: null,
      bad: false,
      href: null,
      open: { call: 'filesAnswerOpen', arg: it.id }
    }
  })
}

// Open one item where it lives: the Files window on that node.
export function filesAnswerOpen(id) {
  if (!id) return false
  this.call('openAppPage', '/files?node=' + encodeURIComponent(String(id)))
  return true
}
